/* Birthstone Finder Tool — Core logic
 * Public API: window.BirthstoneFinder.init(rootEl, options)
 *
 * Spec:   tools/ifshe/002-birthstone-finder/design-spec.md
 * Data:   tools/ifshe/002-birthstone-finder ... research/birthstone/birthstone-data-sources.md
 *   US modern  = American Gem Society (americangemsociety.org/birthstones)
 *   UK list    = National Association of Goldsmiths 1937 standard
 *   Zodiac     = traditional Western zodiac (NOT canonical — labelled "traditional")
 * Stone identity per month/sign traces to those sources. Colours are factual
 * gemstone hues; meanings are traditional symbolism (rendered as "traditionally
 * associated with", never claimed as canonical fact).
 */
(function () {
  'use strict';

  // ---------- DATA: 12-month birthstones (US modern vs UK traditional) ----------
  // us[0] / uk[0] = the primary (headline) stone for that locale.
  // When us[0] !== uk[0] the month is a true primary "flip" (March, December).
  const MONTHS = [
    { m: 1,  name: "January",   us: ["Garnet"],                          uk: ["Garnet"],
      color: "Deep red",                 hex: "#7b1f2b",
      meaning: "protection, trust and lasting friendship" },
    { m: 2,  name: "February",  us: ["Amethyst"],                        uk: ["Amethyst"],
      color: "Purple / violet",          hex: "#8a5fb0",
      meaning: "calm, clarity and inner strength" },
    { m: 3,  name: "March",     us: ["Aquamarine", "Bloodstone"],        uk: ["Bloodstone", "Aquamarine"],
      color: "Sea-blue & green",         hex: "#86c5d6",
      meaning: "courage, calm and clear communication",
      note: "The US modern list leads with Aquamarine, while the UK traditional (NAG 1937) list leads with Bloodstone. Both stones belong to March in both countries — only the primary differs." },
    { m: 4,  name: "April",     us: ["Diamond"],                         uk: ["Diamond"],
      color: "Clear / white",            hex: "#dfe4ea",
      meaning: "enduring love, clarity and strength" },
    { m: 5,  name: "May",       us: ["Emerald"],                         uk: ["Emerald"],
      color: "Lush green",               hex: "#1f9a6d",
      meaning: "rebirth, love and foresight" },
    { m: 6,  name: "June",      us: ["Pearl", "Moonstone", "Alexandrite"], uk: ["Pearl", "Moonstone", "Alexandrite"],
      color: "Creamy white / iridescent", hex: "#eef0f2",
      meaning: "purity, calm and new beginnings" },
    { m: 7,  name: "July",      us: ["Ruby"],                            uk: ["Ruby"],
      color: "Vivid red",                hex: "#c41e3a",
      meaning: "passion, vitality and protection" },
    { m: 8,  name: "August",    us: ["Peridot", "Spinel", "Sardonyx"],   uk: ["Peridot", "Sardonyx"],
      color: "Lime green",               hex: "#9ac23a",
      meaning: "strength, prosperity and good health",
      note: "Peridot is the primary August stone in both countries. The US modern list also recognises Spinel (added in 2016), which the UK 1937 list does not include." },
    { m: 9,  name: "September", us: ["Sapphire"],                        uk: ["Sapphire"],
      color: "Deep blue",                hex: "#1c3f94",
      meaning: "wisdom, loyalty and nobility" },
    { m: 10, name: "October",   us: ["Opal", "Tourmaline"],              uk: ["Opal", "Tourmaline"],
      color: "Iridescent",               hex: "#bcd2cf",
      meaning: "hope, creativity and faithfulness",
      note: "Both countries name Opal as the lead October stone. The US modern list treats Tourmaline as an equal birthstone; the UK traditional list keeps Opal primary with Tourmaline as an alternative." },
    { m: 11, name: "November",  us: ["Topaz", "Citrine"],                uk: ["Topaz", "Citrine"],
      color: "Golden yellow / amber",    hex: "#e2a52a",
      meaning: "warmth, abundance and healing" },
    { m: 12, name: "December",  us: ["Tanzanite", "Turquoise", "Zircon"], uk: ["Turquoise", "Zircon", "Tanzanite"],
      color: "Blue / blue-violet",       hex: "#4a6fc4",
      meaning: "good fortune, success and calm",
      note: "The US modern list leads with Tanzanite (added in 2002), while the UK traditional list leads with Turquoise. All three stones belong to December — only the primary differs." }
  ];

  // ---------- DATA: 12 zodiac signs (traditional Western, non-canonical) ----------
  // Date ranges use the standard Western tropical zodiac. Capricorn wraps the year.
  const ZODIAC = [
    { sign: "Capricorn",   from: [12, 22], to: [1, 19],  dates: "Dec 22 – Jan 19",
      stone: "Garnet",     color: "Deep red",          hex: "#7b1f2b", meaning: "ambition, discipline and resilience" },
    { sign: "Aquarius",    from: [1, 20],  to: [2, 18],  dates: "Jan 20 – Feb 18",
      stone: "Amethyst",   color: "Purple",            hex: "#8a5fb0", meaning: "originality, vision and calm" },
    { sign: "Pisces",      from: [2, 19],  to: [3, 20],  dates: "Feb 19 – Mar 20",
      stone: "Aquamarine", color: "Sea-blue",          hex: "#86c5d6", meaning: "compassion, intuition and serenity" },
    { sign: "Aries",       from: [3, 21],  to: [4, 19],  dates: "Mar 21 – Apr 19",
      stone: "Diamond",    color: "Clear / white",     hex: "#dfe4ea", meaning: "courage, energy and passion", also: "Bloodstone" },
    { sign: "Taurus",      from: [4, 20],  to: [5, 20],  dates: "Apr 20 – May 20",
      stone: "Emerald",    color: "Lush green",        hex: "#1f9a6d", meaning: "love, loyalty and stability" },
    { sign: "Gemini",      from: [5, 21],  to: [6, 20],  dates: "May 21 – Jun 20",
      stone: "Pearl",      color: "Creamy white",      hex: "#eef0f2", meaning: "adaptability, warmth and clear thought", also: "Agate" },
    { sign: "Cancer",      from: [6, 21],  to: [7, 22],  dates: "Jun 21 – Jul 22",
      stone: "Ruby",       color: "Vivid red",         hex: "#c41e3a", meaning: "devotion, protection and home" },
    { sign: "Leo",         from: [7, 23],  to: [8, 22],  dates: "Jul 23 – Aug 22",
      stone: "Peridot",    color: "Lime green",        hex: "#9ac23a", meaning: "confidence, warmth and vitality" },
    { sign: "Virgo",       from: [8, 23],  to: [9, 22],  dates: "Aug 23 – Sep 22",
      stone: "Sapphire",   color: "Deep blue",         hex: "#1c3f94", meaning: "wisdom, focus and loyalty" },
    { sign: "Libra",       from: [9, 23],  to: [10, 22], dates: "Sep 23 – Oct 22",
      stone: "Opal",       color: "Iridescent",        hex: "#bcd2cf", meaning: "balance, harmony and creativity" },
    { sign: "Scorpio",     from: [10, 23], to: [11, 21], dates: "Oct 23 – Nov 21",
      stone: "Topaz",      color: "Golden",            hex: "#e2a52a", meaning: "intensity, loyalty and transformation" },
    { sign: "Sagittarius", from: [11, 22], to: [12, 21], dates: "Nov 22 – Dec 21",
      stone: "Tanzanite",  color: "Blue-violet",       hex: "#4a6fc4", meaning: "optimism, adventure and honesty", also: "Turquoise" }
  ];

  const DAYS_IN_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]; // Feb=29 (no year collected)

  // ---------- CORE: lookups ----------
  function monthByNumber(m) {
    return MONTHS.find(function (x) { return x.m === m; }) || null;
  }
  function zodiacFor(month, day) {
    for (var i = 0; i < ZODIAC.length; i++) {
      var z = ZODIAC[i];
      var fm = z.from[0], fd = z.from[1], tm = z.to[0], td = z.to[1];
      if (fm <= tm) {
        if ((month === fm && day >= fd) || (month === tm && day <= td) ||
            (month > fm && month < tm)) return z;
      } else { // wraps year-end (Capricorn)
        if ((month === fm && day >= fd) || (month === tm && day <= td) ||
            month > fm || month < tm) return z;
      }
    }
    return null;
  }

  // ---------- GA4 event ----------
  function track(event, params) {
    try {
      if (window.gtag) window.gtag('event', event, params || {});
      else if (window.dataLayer) window.dataLayer.push(Object.assign({ event: event }, params || {}));
    } catch (_) {}
  }

  // ---------- UI Controller ----------
  function init(root, opts) {
    opts = opts || {};
    var isUK = (opts.locale === 'en-GB');
    var W = {
      jewellery: isUK ? 'jewellery' : 'jewelry',
      colour:    isUK ? 'colour'    : 'color',
      Colour:    isUK ? 'Colour'    : 'Color'
    };

    // Pick the locale-appropriate stone list for a month object.
    function localeStones(mo) { return isUK ? mo.uk : mo.us; }
    function primaryStone(mo) { return localeStones(mo)[0]; }

    // ----- Elements -----
    var modeBtns = root.querySelectorAll('[data-bsf-mode]');
    var panels = root.querySelectorAll('[data-bsf-panel]');
    var resultEl = root.querySelector('[data-bsf-result]');
    var eyebrowEl = root.querySelector('[data-bsf-eyebrow]');
    var primaryBlock = root.querySelector('[data-bsf-primary]');
    var duoBlock = root.querySelector('[data-bsf-duo]');
    var standardsBlock = root.querySelector('[data-bsf-standards]');
    var divergeBlock = root.querySelector('[data-bsf-diverge]');
    var hintEl = root.querySelector('[data-bsf-hint]');
    var shopCta = root.querySelector('[data-bsf-shop-cta]');
    var shopHeading = root.querySelector('[data-bsf-shop-heading]');
    var shopLink = root.querySelector('[data-bsf-shop-link]');

    // ----- Mode switching -----
    function activateMode(name, fromUser) {
      modeBtns.forEach(function (b) { b.classList.toggle('is-active', b.dataset.bsfMode === name); });
      panels.forEach(function (p) { p.classList.toggle('is-active', p.dataset.bsfPanel === name); });
      if (fromUser) track('tool_mode_view', { mode: name });
    }
    modeBtns.forEach(function (b) {
      b.addEventListener('click', function () { activateMode(b.dataset.bsfMode, true); });
    });

    // ----- Render helpers -----
    function swatchHtml(hex) {
      return '<span class="bsf-swatch" style="background:' + hex + '"></span>';
    }
    function stoneHtml(name, color, meaning) {
      return '' +
        '<div class="bsf-stone-text">' +
          '<p class="bsf-stone-name">' + name + '</p>' +
          '<p class="bsf-stone-color">' + W.Colour + ': ' + color + '</p>' +
          '<p class="bsf-stone-meaning"><em>Traditionally associated with:</em> ' + meaning + '</p>' +
        '</div>';
    }

    function renderStandards(mo, z) {
      var html = '';
      html += '<div class="bsf-std"><span class="bsf-std-label">US (modern)</span>' +
              '<span class="bsf-std-value">' + mo.us.join(', ') + '</span></div>';
      html += '<div class="bsf-std"><span class="bsf-std-label">UK (traditional)</span>' +
              '<span class="bsf-std-value">' + mo.uk.join(', ') + '</span></div>';
      if (z) {
        html += '<div class="bsf-std"><span class="bsf-std-label">Zodiac (' + z.sign + ')</span>' +
                '<span class="bsf-std-value">' + z.stone + '</span></div>';
      }
      standardsBlock.innerHTML = html;
    }

    function renderDiverge(mo) {
      var flip = mo.us[0] !== mo.uk[0];
      if (!mo.note && !flip) { divergeBlock.classList.remove('is-visible'); divergeBlock.innerHTML = ''; return; }
      var html = '<p class="bsf-diverge-head">US &amp; UK birthstones for ' + mo.name + ' differ</p>';
      if (flip) {
        html += '<div class="bsf-diverge-pair">' +
                  '<div><span class="bsf-std-label">US modern</span><div class="bsf-std-value">' + mo.us[0] + '</div></div>' +
                  '<div><span class="bsf-std-label">UK traditional</span><div class="bsf-std-value">' + mo.uk[0] + '</div></div>' +
                '</div>';
      }
      if (mo.note) html += '<p class="bsf-diverge-note">' + mo.note + '</p>';
      divergeBlock.innerHTML = html;
      divergeBlock.classList.add('is-visible');
    }

    function showShop(stoneName) {
      if (!shopCta) return;
      shopCta.classList.remove('bsf-hidden');
      if (shopHeading) shopHeading.textContent = 'Shop ' + stoneName + ' ' + W.jewellery + ' at ifshe';
      if (shopLink) shopLink.dataset.bsfStone = stoneName;
    }

    function reveal(eyebrow) {
      eyebrowEl.textContent = eyebrow;
      resultEl.classList.add('is-visible');
      setTimeout(function () { resultEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); }, 50);
    }

    // ----- Result renderers (one per mode) -----
    function showMonthResult(mo) {
      var stone = primaryStone(mo);
      duoBlock.classList.add('bsf-hidden');
      primaryBlock.classList.remove('bsf-hidden');
      primaryBlock.innerHTML = swatchHtml(mo.hex) + stoneHtml(stone, mo.color, mo.meaning);
      renderStandards(mo, null);
      renderDiverge(mo);
      hintEl.textContent = '';
      showShop(stone);
      reveal('Your ' + mo.name + ' birthstone');
      track('tool_birthstone_found', { mode: 'month', month: mo.name, sign: '', stone: stone });
    }

    function showDateResult(mo, z) {
      var monthStone = primaryStone(mo);
      primaryBlock.classList.add('bsf-hidden');
      duoBlock.classList.remove('bsf-hidden');
      duoBlock.innerHTML =
        '<div class="bsf-duo-card">' +
          '<p class="bsf-duo-label">Your month birthstone (' + mo.name + ')</p>' +
          '<div class="bsf-duo-stone">' + swatchHtml(mo.hex) +
            '<div class="bsf-stone-text"><p class="bsf-stone-name">' + monthStone + '</p>' +
            '<p class="bsf-stone-color">' + W.Colour + ': ' + mo.color + '</p></div>' +
          '</div>' +
        '</div>' +
        '<div class="bsf-duo-card">' +
          '<p class="bsf-duo-label">Your zodiac stone (' + z.sign + ')</p>' +
          '<div class="bsf-duo-stone">' + swatchHtml(z.hex) +
            '<div class="bsf-stone-text"><p class="bsf-stone-name">' + z.stone + '</p>' +
            '<p class="bsf-stone-color">' + W.Colour + ': ' + z.color + '</p></div>' +
          '</div>' +
        '</div>';
      renderStandards(mo, z);
      renderDiverge(mo);
      hintEl.innerHTML = 'Your month stone is <strong>' + monthStone + '</strong> and your ' + z.sign +
        ' zodiac stone is <strong>' + z.stone + '</strong>' +
        (monthStone === z.stone ? ' — they match!' : '. Many people wear either, or both.');
      showShop(monthStone);
      reveal('Born ' + mo.name + ' ' + z._day + ' · ' + z.sign);
      track('tool_birthstone_found', { mode: 'date', month: mo.name, sign: z.sign, stone: monthStone });
    }

    function showZodiacResult(z) {
      duoBlock.classList.add('bsf-hidden');
      primaryBlock.classList.remove('bsf-hidden');
      var meaning = z.meaning + (z.also ? ' (some traditions also use ' + z.also + ')' : '');
      primaryBlock.innerHTML = swatchHtml(z.hex) + stoneHtml(z.stone, z.color, meaning);
      standardsBlock.innerHTML =
        '<div class="bsf-std"><span class="bsf-std-label">Sign</span><span class="bsf-std-value">' + z.sign + '</span></div>' +
        '<div class="bsf-std"><span class="bsf-std-label">Dates</span><span class="bsf-std-value">' + z.dates + '</span></div>' +
        '<div class="bsf-std"><span class="bsf-std-label">Zodiac stone</span><span class="bsf-std-value">' + z.stone + '</span></div>';
      divergeBlock.classList.remove('is-visible'); divergeBlock.innerHTML = '';
      hintEl.innerHTML = 'Zodiac stones are a traditional, decorative system — they are not officially governed. ' +
        'Your <a href="#" data-bsf-jump="date">month birthstone may differ</a>, so check by your exact birth date too.';
      showShop(z.stone);
      reveal('Your ' + z.sign + ' zodiac stone');
      track('tool_birthstone_found', { mode: 'zodiac', month: '', sign: z.sign, stone: z.stone });
    }

    // ----- Mode 1: By Month -----
    var monthSel = root.querySelector('[data-bsf-month-select]');
    if (monthSel) {
      monthSel.addEventListener('change', function () {
        var m = parseInt(monthSel.value, 10);
        if (!m) return;
        showMonthResult(monthByNumber(m));
      });
    }

    // ----- Mode 2: By Birth Date -----
    var dateMonthSel = root.querySelector('[data-bsf-date-month]');
    var dateDaySel = root.querySelector('[data-bsf-date-day]');
    var dateErr = root.querySelector('[data-bsf-date-error]');

    function rebuildDays() {
      if (!dateMonthSel || !dateDaySel) return;
      var m = parseInt(dateMonthSel.value, 10);
      var max = m ? DAYS_IN_MONTH[m - 1] : 31;
      var cur = parseInt(dateDaySel.value, 10) || 0;
      var html = '<option value="">Day</option>';
      for (var d = 1; d <= max; d++) {
        html += '<option value="' + d + '"' + (d === cur ? ' selected' : '') + '>' + d + '</option>';
      }
      dateDaySel.innerHTML = html;
    }
    function tryDate() {
      if (!dateMonthSel || !dateDaySel) return;
      var m = parseInt(dateMonthSel.value, 10);
      var d = parseInt(dateDaySel.value, 10);
      if (!m || !d) { if (dateErr) dateErr.textContent = ''; return; }
      var z = zodiacFor(m, d);
      if (!z) { if (dateErr) dateErr.textContent = 'Please pick a valid month and day.'; return; }
      if (dateErr) dateErr.textContent = '';
      z._day = d;
      showDateResult(monthByNumber(m), z);
    }
    if (dateMonthSel) dateMonthSel.addEventListener('change', function () { rebuildDays(); tryDate(); });
    if (dateDaySel) dateDaySel.addEventListener('change', tryDate);

    // ----- Mode 3: By Zodiac Sign -----
    var zodiacSel = root.querySelector('[data-bsf-zodiac-select]');
    if (zodiacSel) {
      zodiacSel.addEventListener('change', function () {
        var sign = zodiacSel.value;
        if (!sign) return;
        var z = ZODIAC.find(function (x) { return x.sign === sign; });
        if (z) showZodiacResult(z);
      });
    }

    // ----- "month birthstone may differ" jump link (delegated) -----
    root.addEventListener('click', function (e) {
      var jump = e.target.closest('[data-bsf-jump]');
      if (jump) { e.preventDefault(); activateMode(jump.dataset.bsfJump, true); }
    });

    // ----- Shop / gift CTA tracking -----
    if (shopLink) {
      shopLink.addEventListener('click', function () {
        track('tool_shop_cta_click', { stone: shopLink.dataset.bsfStone || '', collection: shopLink.dataset.bsfCollection || '' });
      });
    }
    var giftLink = root.querySelector('[data-bsf-gift-link]');
    if (giftLink) {
      giftLink.addEventListener('click', function () {
        track('tool_gift_cta_click', { target: giftLink.dataset.bsfGift || 'mom' });
      });
    }
    var chartEl = root.querySelector('[data-bsf-chart]');
    if (chartEl) {
      chartEl.addEventListener('toggle', function () { if (chartEl.open) track('tool_chart_expand', {}); });
    }

    // ----- Initial state: locale-default mode -----
    rebuildDays();
    activateMode(isUK ? 'zodiac' : 'date', false);
  }

  window.BirthstoneFinder = { init: init, MONTHS: MONTHS, ZODIAC: ZODIAC, zodiacFor: zodiacFor };
})();
